import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Project, ProjectSummary } from '@/types'

import { ArrowRight } from 'lucide-react'
import { getProjectSlugs, getProjectWithNext } from '@/lib/content'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Media } from '@/components/generals/media'
import { UmbraLink } from '@/components/generals/umbra-link'
import Gravity from '@/components/universe/gravity'
import { GlassRing } from '@/components/landing/project/glass-ring'
import { ObjectStill } from '@/components/landing/project/object-still'

interface ProjectPageProps {
    project: Project
    next: ProjectSummary | null
}

// Long names get smaller so the title always fits across the window
const titleSize = (title: string, widest: number): string => {
    const vw = Math.min(widest, 92 / Math.max(1, title.length * 0.7))

    return `clamp(3rem, ${vw.toFixed(2)}vw, 24rem)`
}

const ProjectPage: FC<ProjectPageProps> = ({ project, next = null }): ReactNode => {
    if (!project) return null

    const slate = [
        { label: "Client", value: project.client },
        { label: "Year", value: String(project.year) },
        { label: "Role", value: project.role },
        { label: "Duration", value: project.duration }
    ].filter(line => line.value)

    // Projects without their own sequence still show their cover between the title and Next
    const images = project.sequence.length > 0
        ? project.sequence
        : project.cover ? [{ src: project.cover, alt: project.coverAlt ?? null }] : []

    return (
        <Layout>
            <Seo
                title={project.title}
                description={project.summary ?? `${project.title}, ${project.year}. ${project.role ?? 'Work'} by VOIDEN.`}
                ogImage={project.cover ?? undefined}
            />

            <section id='title' className='relative min-h-[100svh] px-6 pb-[14vh] pt-[16vh] md:px-[6vw] md:pt-[13vh]'>
                {(project.objectImage || project.glassRing) && (
                    <div className='mx-auto mb-[-6vh] w-[86vw] md:absolute md:right-[3vw] md:top-[7vh] md:mb-0 md:w-[min(38vw,76vh)]'>
                        {project.objectImage
                            ? <ObjectStill src={project.objectImage} alt={project.objectImageAlt || ''} />
                            : <GlassRing />}
                    </div>
                )}

                <div className='relative'>
                    <Gravity
                        as='h1'
                        kind='text'
                        fall={false}
                        delay={200}
                        style={{ fontSize: titleSize(project.title, 16) }}
                        className='display-serif uppercase text-foreground'
                    >
                        {project.title}
                    </Gravity>

                    <Gravity
                        as='dl'
                        kind='text'
                        fall={false}
                        delay={800}
                        className='mono-note mt-[5vh] grid w-max grid-cols-[auto_auto] gap-x-3 gap-y-1 md:ml-[0.4vw]'
                    >
                        {slate.map(line => (
                            <div key={line.label} className='contents'>
                                <dt className='text-muted-foreground'>
                                    {line.label}
                                    <span aria-hidden='true' className='ml-3 text-foreground/25'>/</span>
                                </dt>

                                <dd className='text-foreground/85'>
                                    {line.value}
                                </dd>
                            </div>
                        ))}
                    </Gravity>
                </div>
            </section>

            <div id='images'>
                {images.map((image, index) => (
                    <figure key={image.src} className='px-4 py-[14vh] md:px-[6vw] md:py-[18vh]'>
                        <Gravity kind='image' className='relative aspect-[4/5] w-full overflow-hidden bg-umber/15 md:aspect-[16/9]'>
                            <Media
                                src={image.src}
                                alt={image.alt || `${project.title}, image ${index + 1}`}
                                sizes='(min-width: 768px) 88vw, 100vw'
                                grade={index % 2 === 0 ? 'left' : 'right'}
                            />
                        </Gravity>

                        {index === 0 && project.caption && (
                            <Gravity as='figcaption' kind='text' delay={300} className='mono-note mt-5 max-w-[42ch] text-muted-foreground'>
                                {project.caption}
                            </Gravity>
                        )}
                    </figure>
                ))}
            </div>

            {next && (
                <section id='next' className='flex min-h-[100svh] flex-col justify-center px-6 md:px-[6vw]'>
                    <Gravity kind='text' fall={false} className='mono-note mb-6 text-muted-foreground'>
                        Next
                    </Gravity>

                    <Gravity kind='text' fall={false} delay={200}>
                        <UmbraLink
                            href={`/work/${next.slug}`}
                            className='group inline-flex items-center gap-[0.18em] text-foreground transition-colors duration-700 hover:text-flare focus-visible:text-flare'
                            style={{ fontSize: titleSize(next.title, 15) }}
                        >
                            <span className='display-serif amber-line'>
                                {next.title}
                            </span>

                            <ArrowRight
                                aria-hidden='true'
                                strokeWidth={1}
                                className='size-6 shrink-0 translate-y-[0.1em] text-muted-foreground transition-[translate,color] duration-700 group-hover:translate-x-2 group-hover:text-flare md:size-8'
                            />
                        </UmbraLink>
                    </Gravity>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getProjectSlugs()

        return {
            paths: slugs.map(slug => ({ params: { slug } })),
            fallback: 'blocking'
        }
    }
    catch (error) {
        console.error('Project paths could not be loaded:', error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<ProjectPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!slug) return { notFound: true }

    try {
        const result = await getProjectWithNext(slug)

        if (!result) return { notFound: true, revalidate: 60 }

        return {
            props: { project: result.project, next: result.next },
            revalidate: 60
        }
    }
    catch (error) {
        console.error(`Project "${slug}" could not be loaded:`, error)

        return { notFound: true, revalidate: 10 }
    }
}

export default ProjectPage
